import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { campaignKeys } from '../queryKeys';
import { errorMessage } from '../../copy';
import {
  listShareGrants,
  mintShareGrant,
  revokeShareGrant,
  updateShareGrant,
  type MintShareGrantInput,
  type ShareGrantDto,
  type UpdateShareGrantInput,
  type UpdateShareGrantResult,
} from '../api/shareGrants';

/**
 * useShareGrants — every share grant ever minted on the campaign (incl.
 * revoked/expired), newest first. Powers the "Manage share links" panel AND
 * SharePopover's active-grant check (the latter passes `enabled: canShare &&
 * flagOn` so a non-admin surface, or the flag being off, never fires the
 * request at all — mirrors useLastImportResult's gating pattern).
 */
export function useShareGrants(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<ShareGrantDto[]>({
    queryKey: campaignKeys.shareGrants(campaignId),
    queryFn: () => listShareGrants(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
  });
}

/**
 * useMintShareGrant — mints (or re-mints) a public, view-only share grant
 * for one campaign-detail screen. Invalidates the share-grants list so the
 * new/refreshed grant shows up immediately. Toasts on success/error
 * unconditionally, same as useRevokeShareGrant/useUpdateShareGrant — callers
 * that need to react to the outcome themselves (e.g. SharePopover keeping
 * its dialog open on failure) use `mutateAsync` and catch the rejection
 * rather than suppressing this hook's own toast.
 */
export function useMintShareGrant(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<ShareGrantDto, Error, MintShareGrantInput>({
    mutationFn: (input) => mintShareGrant(campaignId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.shareGrants(campaignId) });
      toast.success('Share link created');
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to create share link')),
  });
}

/**
 * useRevokeShareGrant — revokes a share grant by id. Invalidates the
 * share-grants list so the row flips to "revoked" immediately.
 */
export function useRevokeShareGrant(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (grantId) => revokeShareGrant(campaignId, grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.shareGrants(campaignId) });
      toast.success('Share link revoked');
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to revoke share link')),
  });
}

/** Mutation variables for {@link useUpdateShareGrant} — bundles the target
 *  grant id with the PATCH body so the hook's call sites read as one object,
 *  matching the shape callers already build (grant + edited fields). */
export interface UpdateShareGrantVariables {
  grantId: string;
  input: UpdateShareGrantInput;
}

/**
 * useUpdateShareGrant — edits a share grant's TTL and/or password (PATCH).
 * Invalidates the same `shareGrants` query key as mint/revoke, so both the
 * Links tab AND SharePopover's active-grant check refresh from the one
 * shared cache entry after an edit. Does NOT merge the mutation's response
 * into the cache directly — `password_protected` is only present on the
 * response when the request definitively changed it (see
 * `UpdateShareGrantResult`'s doc comment), so an invalidate-and-refetch is
 * the only way to reliably reflect the grant's new state everywhere.
 */
export function useUpdateShareGrant(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<UpdateShareGrantResult, Error, UpdateShareGrantVariables>({
    mutationFn: ({ grantId, input }) => updateShareGrant(campaignId, grantId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.shareGrants(campaignId) });
      toast.success('Share link updated');
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to update share link')),
  });
}
